// src/pages/EditArticlePage/EditArticlePage.tsx

import { useState, useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useArticle } from '../../hooks/useArticle'
import { useArticles } from '../../hooks/useArticles'
import * as articleService from '../../services/articleService'
import { AVAILABLE_CATEGORIES, AVAILABLE_TAGS } from '../../constants/articleOptions'
import styles from './EditArticlePage.module.css'

// Import the markdown editor
import MDEditor from '@uiw/react-md-editor'
import '@uiw/react-md-editor/markdown-editor.css'

/* ─────────────────────────────────────────────────
   Icônes SVG inline
───────────────────────────────────────────────── */

const IconArrowLeft = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={styles.backIcon}>
    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
  </svg>
)

/** Réplique l'algorithme GenerateSlug de l'API C#. */
function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // supprime les diacritiques
    .normalize('NFC')
    .replace(/[?!]/g, '')
    .replace(/[\s']+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '')
}

export function EditArticlePage() {
  const { id } = useParams<{ id: string }>()
  const { token, user } = useAuth()
  const navigate = useNavigate()
  const { article, loading: articleLoading, error: articleError } = useArticle(id!)
  const { refetch } = useArticles()

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [content, setContent] = useState('')
  const [cover, setCover] = useState<File | null>(null)
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isAdmin = user?.roles?.includes('Admin')

  // Populate form when article loads
  useEffect(() => {
    if (article) {
      setTitle(article.title)
      setDescription(article.description)
      setContent(article.content || '')
      setSelectedCategories(article.categories || [])
      setSelectedTags(article.tags || [])
    }
  }, [article])

  // Redirect if not admin
  useEffect(() => {
    if (!isAdmin) {
      navigate('/blog')
    }
  }, [isAdmin, navigate])

  if (articleLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.inner}>
          <div className={styles.loading}>Chargement de l'article...</div>
        </div>
      </div>
    )
  }

  if (articleError || !article) {
    return (
      <div className={styles.page}>
        <div className={styles.inner}>
          <Link to="/blog" className={styles.back}>
            <IconArrowLeft /> Retour au blog
          </Link>
          <div className={styles.error}>
            Article introuvable ou erreur de chargement.
          </div>
        </div>
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token) return

    setLoading(true)
    setError(null)

    try {
      const payload: articleService.UpdateArticlePayload = {
        title,
        description,
        content,
        cover: article.cover,
        newCoverFile: cover || undefined,
        author: article.authorId ?? '',
        isPublished: article.isPublished,
        categories: selectedCategories,
        tags: selectedTags,
      }

      await articleService.updateArticle(token, id!, payload)
      refetch()
      navigate(`/blog/${generateSlug(title)}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la mise à jour')
    } finally {
      setLoading(false)
    }
  }

  const handleCategoryChange = (category: string, checked: boolean) => {
    setSelectedCategories(prev =>
      checked ? [...prev, category] : prev.filter(c => c !== category)
    )
  }

  const handleTagChange = (tag: string, checked: boolean) => {
    setSelectedTags(prev =>
      checked ? [...prev, tag] : prev.filter(t => t !== tag)
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.inner}>

        {/* Header */}
        <div className={styles.header}>
          <Link to={`/blog/${article.slug}`} className={styles.back}>
            <IconArrowLeft />
            Retour à l'article
          </Link>
          <h1 className={styles.title}>Modifier l'article</h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className={styles.form}>

          {/* Title */}
          <div className={styles.field}>
            <label htmlFor="title" className={styles.label}>Titre</label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={styles.input}
              required
            />
          </div>

          {/* Description */}
          <div className={styles.field}>
            <label htmlFor="description" className={styles.label}>Description</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={styles.textarea}
              rows={3}
              required
            />
          </div>

          {/* Content */}
          <div className={styles.field}>
            <label className={styles.label}>Contenu (Markdown)</label>
            <div data-color-mode="light" className={styles.editor}>
              <MDEditor
                value={content}
                onChange={(val) => setContent(val || '')}
                preview="edit"
                hideToolbar={false}
                visibleDragbar={false}
                height={400}
              />
            </div>
          </div>

          {/* Cover Image */}
          <div className={styles.field}>
            <label htmlFor="cover" className={styles.label}>Image de couverture</label>
            <input
              id="cover"
              type="file"
              accept="image/*"
              onChange={(e) => setCover(e.target.files?.[0] || null)}
              className={styles.fileInput}
            />
            {article.cover && !cover && (
              <p className={styles.coverNote}>
                Image actuelle : <img src={article.cover} alt="Couverture actuelle" className={styles.coverPreview} />
              </p>
            )}
          </div>

          {/* Categories */}
          <div className={styles.field}>
            <label className={styles.label}>
              Catégories
              {selectedCategories.length > 0 && (
                <span className={styles.selectedCount}> ({selectedCategories.length} sélectionnée{selectedCategories.length > 1 ? 's' : ''})</span>
              )}
            </label>
            <div className={styles.checkboxGroup}>
              {AVAILABLE_CATEGORIES.map((category) => (
                <label key={category} className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(category)}
                    onChange={(e) => handleCategoryChange(category, e.target.checked)}
                    disabled={loading}
                  />
                  {category}
                </label>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div className={styles.field}>
            <label className={styles.label}>
              Tags
              {selectedTags.length > 0 && (
                <span className={styles.selectedCount}> ({selectedTags.length} sélectionné{selectedTags.length > 1 ? 's' : ''})</span>
              )}
            </label>
            <div className={styles.checkboxGroup}>
              {AVAILABLE_TAGS.map((tag) => (
                <label key={tag} className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={selectedTags.includes(tag)}
                    onChange={(e) => handleTagChange(tag, e.target.checked)}
                    disabled={loading}
                  />
                  {tag}
                </label>
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className={styles.error}>
              {error}
            </div>
          )}

          {/* Submit */}
          <div className={styles.actions}>
            <button
              type="submit"
              disabled={loading}
              className={styles.submitBtn}
            >
              {loading ? 'Mise à jour...' : "Mettre à jour l'article"}
            </button>
          </div>

        </form>

      </div>
    </div>
  )
}
